import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  writeBatch,
  serverTimestamp,
  increment
} from 'firebase/firestore';

const INVITE_CODES_COLLECTION = 'inviteCodes';
const ACCESS_LOGS_COLLECTION = 'accessLogs';
const RATE_LIMIT_COLLECTION = 'rateLimits';

// Rate limiting configuration
const RATE_LIMIT_CONFIG = {
  maxAttempts: 5, // Max validation attempts per hour
  windowMs: 60 * 60 * 1000, // 1 hour in milliseconds
};

/**
 * Check rate limiting for code validation attempts
 * @param {string} deviceId - Device identifier
 * @returns {Promise<{allowed: boolean, remainingAttempts?: number}>}
 */
async function checkRateLimit(deviceId) {
  try {
    const rateLimitRef = doc(db, RATE_LIMIT_COLLECTION, deviceId);
    const rateLimitDoc = await getDoc(rateLimitRef);
    
    const now = Date.now();
    const windowStart = now - RATE_LIMIT_CONFIG.windowMs;
    
    if (!rateLimitDoc.exists()) {
      // First attempt - create rate limit record
      await setDoc(rateLimitRef, {
        attempts: 1,
        firstAttempt: now,
        lastAttempt: now,
        resetAt: now + RATE_LIMIT_CONFIG.windowMs
      });
      return { allowed: true, remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts - 1 };
    }
    
    const data = rateLimitDoc.data();
    
    // Check if window has expired
    if (data.resetAt && now > data.resetAt) {
      // Reset the counter
      await updateDoc(rateLimitRef, {
        attempts: 1,
        firstAttempt: now,
        lastAttempt: now,
        resetAt: now + RATE_LIMIT_CONFIG.windowMs
      });
      return { allowed: true, remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts - 1 };
    }
    
    // Check if limit exceeded
    if (data.attempts >= RATE_LIMIT_CONFIG.maxAttempts) {
      const resetIn = Math.ceil((data.resetAt - now) / 1000 / 60); // minutes
      return { 
        allowed: false, 
        remainingAttempts: 0,
        resetInMinutes: resetIn
      };
    }
    
    // Increment attempt counter
    await updateDoc(rateLimitRef, {
      attempts: increment(1),
      lastAttempt: now
    });
    
    return { 
      allowed: true, 
      remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts - data.attempts - 1 
    };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // Allow on error to avoid blocking legitimate users
    return { allowed: true };
  }
}

/**
 * Check if an invite code is valid and unused
 * @param {string} code - The invite code to validate
 * @param {string} deviceId - Device identifier for rate limiting
 * @returns {Promise<{valid: boolean, used: boolean, isAdmin?: boolean, code?: string, rateLimited?: boolean, remainingAttempts?: number}>}
 */
export async function validateInviteCode(code, deviceId = null) {
  if (!code || typeof code !== 'string') {
    return { valid: false, used: false };
  }

  try {
    const codeUpper = code.toUpperCase();
    const codeDocRef = doc(db, INVITE_CODES_COLLECTION, codeUpper);
    const codeDoc = await getDoc(codeDocRef);

    if (!codeDoc.exists()) {
      console.log(`[InviteCode] Code not found: ${codeUpper}`);
      return { valid: false, used: false };
    }

    const data = codeDoc.data();
    const isAdmin = data.isAdmin === true;
    
    console.log(`[InviteCode] Code found: ${codeUpper}, isAdmin: ${isAdmin}, used: ${data.used}`);

    // Admin codes bypass rate limiting and "used" check
    if (isAdmin) {
      return {
        valid: true,
        used: false, // Admin codes are never considered "used"
        isAdmin: true,
        code: codeUpper
      };
    }

    // Check rate limiting for regular codes if deviceId provided
    if (deviceId) {
      const rateLimit = await checkRateLimit(deviceId);
      if (!rateLimit.allowed) {
        return { 
          valid: false, 
          used: false, 
          rateLimited: true,
          remainingAttempts: rateLimit.remainingAttempts,
          resetInMinutes: rateLimit.resetInMinutes
        };
      }
    }

    return {
      valid: true,
      used: data.used || false,
      isAdmin: false,
      code: codeUpper,
      remainingAttempts: deviceId ? (await checkRateLimit(deviceId)).remainingAttempts : undefined
    };
  } catch (error) {
    console.error('Error validating invite code:', error);
    return { valid: false, used: false, error: error.message };
  }
}

/**
 * Mark an invite code as used
 * @param {string} code - The invite code to mark as used
 * @param {string} deviceId - Device identifier (for tracking, not validation)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function useInviteCode(code, deviceId = null) {
  if (!code) {
    return { success: false, error: 'Code is required' };
  }

  try {
    const codeUpper = code.toUpperCase();
    const codeDocRef = doc(db, INVITE_CODES_COLLECTION, codeUpper);
    const codeDoc = await getDoc(codeDocRef);

    if (!codeDoc.exists()) {
      return { success: false, error: 'Invalid code' };
    }

    const data = codeDoc.data();
    const isAdmin = data.isAdmin === true;

    // Admin codes are never marked as used - they can be reused
    if (isAdmin) {
      // Just log the access for admin codes, but don't mark as used
      try {
        const logRef = doc(collection(db, ACCESS_LOGS_COLLECTION));
        await setDoc(logRef, {
          code: codeUpper,
          usedAt: serverTimestamp(),
          deviceId: deviceId || null,
          userAgent: navigator.userAgent.substring(0, 100),
          isAdmin: true,
          timestamp: Date.now()
        });
      } catch (logError) {
        console.warn('Failed to log admin access:', logError);
      }
      return { success: true, isAdmin: true };
    }

    // Regular codes - check if already used
    if (data.used) {
      return { success: false, error: 'Code already used' };
    }

    // Mark code as used
    await updateDoc(codeDocRef, {
      used: true,
      usedAt: serverTimestamp(),
      usedBy: deviceId || 'unknown',
      deviceId: deviceId || null
    });

    // Log access for analytics
    try {
      const logRef = doc(collection(db, ACCESS_LOGS_COLLECTION));
      await setDoc(logRef, {
        code: codeUpper,
        usedAt: serverTimestamp(),
        deviceId: deviceId || null,
        userAgent: navigator.userAgent.substring(0, 100),
        timestamp: Date.now()
      });
    } catch (logError) {
      console.warn('Failed to log access:', logError);
    }

    return { success: true };
  } catch (error) {
    console.error('Error using invite code:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Generate a random invite code
 * @param {number} length - Length of the code (default: 8)
 * @returns {string}
 */
export function generateInviteCode(length = 8) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluding confusing chars like 0, O, I, 1
  let code = '';
  for (let i = 0; i < length; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Create a single invite code and store it in the database
 * @param {string} referrerAddress - The wallet address of the user creating the invite (optional)
 * @returns {Promise<{success: boolean, code?: string, error?: string}>}
 */
export async function createSingleInviteCode(referrerAddress = null) {
  try {
    // Generate a unique code
    let code = generateInviteCode(8);
    let attempts = 0;
    const maxAttempts = 10;

    // Check for uniqueness
    while (attempts < maxAttempts) {
      const codeDocRef = doc(db, INVITE_CODES_COLLECTION, code);
      const codeDoc = await getDoc(codeDocRef);
      
      if (!codeDoc.exists()) {
        // Code is unique, save it
        await setDoc(codeDocRef, {
          code: code,
          used: false,
          createdAt: serverTimestamp(),
          createdBy: referrerAddress || 'user',
          referrerAddress: referrerAddress || null,
          type: 'referral'
        });
        
        return { success: true, code: code };
      }
      
      // Code exists, generate a new one
      code = generateInviteCode(8);
      attempts++;
    }

    return { success: false, error: 'Failed to generate unique code after multiple attempts' };
  } catch (error) {
    console.error('Error creating invite code:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Create multiple invite codes in batch
 * @param {number} count - Number of codes to generate
 * @returns {Promise<{success: boolean, codes: string[], error?: string}>}
 */
export async function createInviteCodes(count = 100) {
  const codes = [];
  const batch = writeBatch(db);

  try {
    // Generate unique codes
    const existingCodes = new Set();
    const codeDocs = await getDocs(collection(db, INVITE_CODES_COLLECTION));
    codeDocs.forEach((doc) => {
      existingCodes.add(doc.id);
    });

    let attempts = 0;
    const maxAttempts = count * 10; // Prevent infinite loops

    while (codes.length < count && attempts < maxAttempts) {
      const code = generateInviteCode(8);
      if (!existingCodes.has(code) && !codes.includes(code)) {
        codes.push(code);
        const codeRef = doc(db, INVITE_CODES_COLLECTION, code);
        batch.set(codeRef, {
          code: code,
          used: false,
          createdAt: new Date().toISOString(),
          createdBy: 'admin'
        });
      }
      attempts++;
    }

    if (codes.length < count) {
      return {
        success: false,
        codes: codes,
        error: `Only generated ${codes.length} unique codes out of ${count} requested`
      };
    }

    await batch.commit();
    return { success: true, codes: codes };
  } catch (error) {
    console.error('Error creating invite codes:', error);
    return { success: false, codes: codes, error: error.message };
  }
}

/**
 * Get statistics about invite codes
 * @returns {Promise<{total: number, used: number, available: number}>}
 */
export async function getInviteCodeStats() {
  try {
    const codeDocs = await getDocs(collection(db, INVITE_CODES_COLLECTION));
    let total = 0;
    let used = 0;

    codeDocs.forEach((doc) => {
      total++;
      if (doc.data().used) {
        used++;
      }
    });

    return {
      total,
      used,
      available: total - used
    };
  } catch (error) {
    console.error('Error getting invite code stats:', error);
    return { total: 0, used: 0, available: 0 };
  }
}
